import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../components/ui/Button';
import { InputSwitch } from 'primereact/inputswitch';
import { Skeleton } from 'primereact/skeleton';

import { useRefreshGreeters, useSetGreeter, useTvDevices } from '../../queries/loyalty';

/**
 * Identity Package P5e — the GREETER SCREEN picker.
 *
 * Designates individual screens as permanent "greeters": the Welcome overlay
 * pinned on, a standing scan-to-join invite at the door, while every other
 * screen in the venue keeps running its content and featured rotation exactly
 * as before.
 *
 * PER-DEVICE, not per-venue, and the UI has to make that obvious — the risk
 * with a row of switches inside a venue-wide settings page is that an owner
 * reads it as "turn greeter mode on" and expects one switch to govern the
 * place. Hence one row per screen, each naming the screen, and copy that talks
 * about "this screen".
 *
 * It does NOT touch campaign assignment. Whatever loop the owner has scheduled
 * on that screen keeps playing underneath the invite; greeter mode pins an
 * overlay and nothing more. That is deliberate — a second content path would be
 * a much bigger feature and this one does not need it.
 */
export default function GreeterScreens() {
  const { t } = useTranslation();
  const { data, isLoading } = useTvDevices();
  const setGreeter = useSetGreeter();
  const refresh = useRefreshGreeters();

  const devices = data?.devices ?? [];
  const anyGreeter = devices.some((d) => d.greeter);

  if (isLoading) {
    return (
      <div className="flex flex-col gap-2">
        <Skeleton height="2.5rem" />
        <Skeleton height="2.5rem" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div>
        <div className="lty-field-label">{t('loyalty.tv.greeter_title')}</div>
        <div className="lty-hint">{t('loyalty.tv.greeter_desc')}</div>
      </div>

      {devices.length === 0 ? (
        <div className="lty-hint">{t('loyalty.tv.greeter_no_screens')}</div>
      ) : (
        <div className="lty-greeter-list">
          {devices.map((d) => (
            <div className="lty-greeter-row" key={d.id}>
              {/* Live online state sits next to the switch on purpose: "pinned"
                  and "pinned, but that screen is unplugged" look identical
                  otherwise, and the second one is the support call. */}
              <span
                className={`lty-greeter-dot${d.online ? ' on' : ''}`}
                aria-label={t(d.online ? 'loyalty.tv.greeter_online' : 'loyalty.tv.greeter_offline')}
                title={t(d.online ? 'loyalty.tv.greeter_online' : 'loyalty.tv.greeter_offline')}
              />
              <div className="lty-greeter-name">
                {d.name}
                {d.greeter && <span className="lty-greeter-badge">{t('loyalty.tv.greeter_badge')}</span>}
              </div>
              <InputSwitch
                checked={!!d.greeter}
                disabled={setGreeter.isLoading}
                onChange={(e) => setGreeter.mutate({ id: d.id, greeter: e.value })}
              />
            </div>
          ))}
        </div>
      )}

      {/* Only offered once something is actually pinned — a "re-send" button
          with nothing to re-send is a button that teaches people it does
          nothing. */}
      {anyGreeter && (
        <div className="flex flex-col gap-2">
          <div>
            <Button
              size="small"
              outlined
              icon="pi pi-refresh"
              label={t('loyalty.tv.greeter_refresh')}
              loading={refresh.isLoading}
              onClick={() => refresh.mutate()}
            />
          </div>
          <div className="lty-hint">{t('loyalty.tv.greeter_refresh_hint')}</div>
        </div>
      )}
    </div>
  );
}
